import { useCallback, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useLandingEmbedViewportPin } from '@/hooks/useLandingEmbedViewportPin'
import { ParticipantShareButton } from '@/features/participantShare/ParticipantShareButton'
import type { ParticipantShareInput } from '@/features/participantShare/participant-share'
import type { LandingRouletteSpinsResponse } from '@/lib/landing-roulette'
import {
  executeShareBridgeAction,
  type RouletteShareChannel,
  type RouletteSharePayload,
} from '@/lib/rouletteShareActions'
import styles from './LandingCampaignShareSheet.module.css'

export type LandingCampaignShareSheetResult = {
  spinsAdded: number
  alreadyShared: boolean
  rouletteUnlocked?: boolean
}

type LandingCampaignShareSheetProps = {
  isOpen: boolean
  payload: RouletteSharePayload
  brandStyle?: CSSProperties
  busyLabel?: string
  onClose: () => void
  onConfirmShare: () => Promise<LandingCampaignShareSheetResult>
  balance: LandingRouletteSpinsResponse | null
  storyInput?: Omit<ParticipantShareInput, 'origin'>
}

const SOCIAL_CHANNELS: Array<{
  id: Exclude<RouletteShareChannel, 'copy' | 'copy_message' | 'more'>
  label: string
  icon: string
}> = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'mdi:whatsapp' },
  { id: 'facebook', label: 'Facebook', icon: 'mdi:facebook' },
  { id: 'instagram', label: 'Instagram', icon: 'mdi:instagram' },
  { id: 'tiktok', label: 'TikTok', icon: 'ic:baseline-tiktok' },
]

function errorMessageFromUnknown(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message?: string }).message
    if (typeof msg === 'string' && msg.trim() !== '') {
      return msg
    }
  }
  return 'Não foi possível concluir esta ação. Tente novamente.'
}

export function LandingCampaignShareSheet({
  isOpen,
  payload,
  brandStyle,
  busyLabel = 'Liberando roleta...',
  onClose,
  onConfirmShare,
  balance,
  storyInput,
}: LandingCampaignShareSheetProps) {
  const [sheetError, setSheetError] = useState('')
  const [bridgeBusy, setBridgeBusy] = useState(false)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [copyHighlight, setCopyHighlight] = useState(false)
  const [actionStarted, setActionStarted] = useState(false)
  const [actionHint, setActionHint] = useState('')
  const channelFlowGuardRef = useRef(false)
  const pinStyle = useLandingEmbedViewportPin(isOpen)
  const overlayBusy = bridgeBusy || confirmBusy
  const canUnlock =
    !!balance &&
    (balance.shares_available > 0 ||
      ((balance.paid_purchases > 0 || balance.achievement_spins_earned) &&
        balance.roulette_unlocked === false))

  const handleClose = useCallback(() => {
    if (overlayBusy) {
      return
    }
    setSheetError('')
    setCopyHighlight(false)
    setActionStarted(false)
    setActionHint('')
    onClose()
  }, [onClose, overlayBusy])

  const handleChannel = useCallback(
    async (channel: RouletteShareChannel) => {
      if (overlayBusy || channelFlowGuardRef.current) {
        return
      }
      channelFlowGuardRef.current = true
      setSheetError('')
      setBridgeBusy(true)
      try {
        const bridge = await executeShareBridgeAction(channel, payload)
        if (bridge.status === 'aborted') {
          return
        }
        setActionStarted(true)
        setCopyHighlight(channel === 'copy')
        if (canUnlock) {
          setConfirmBusy(true)
          const result = await onConfirmShare()
          if (result.spinsAdded > 0 || result.rouletteUnlocked) {
            onClose()
            return
          }
          setSheetError('Não há compartilhamentos com bônus disponíveis agora.')
        }
        setActionHint(
          bridge.status === 'copied'
            ? channel === 'instagram' || channel === 'tiktok'
              ? 'Link copiado. Publique no aplicativo e volte para confirmar.'
              : channel === 'copy'
                ? 'Link copiado. Envie para alguém e volte para confirmar.'
                : 'Convite copiado. Envie para alguém e volte para confirmar.'
            : bridge.status === 'opened'
              ? 'Conclua o envio na outra janela e volte para confirmar.'
              : 'Compartilhamento aberto. Confirme abaixo depois de enviar.'
        )
      } catch (err: unknown) {
        setSheetError(errorMessageFromUnknown(err))
      } finally {
        setBridgeBusy(false)
        setConfirmBusy(false)
        channelFlowGuardRef.current = false
      }
    },
    [overlayBusy, payload, canUnlock, onConfirmShare, onClose]
  )

  const confirm = async () => {
    if (!actionStarted || overlayBusy || !canUnlock) return
    setConfirmBusy(true)
    setSheetError('')
    try {
      const result = await onConfirmShare()
      if (result.spinsAdded > 0 || result.rouletteUnlocked) onClose()
      else setSheetError('Não há compartilhamentos com bônus disponíveis agora.')
    } catch (err) {
      setSheetError(errorMessageFromUnknown(err))
    } finally {
      setConfirmBusy(false)
    }
  }

  if (!isOpen || typeof document === 'undefined') {
    return null
  }

  const layerClassName = pinStyle ? `${styles.layer} ${styles.layerPinned}` : styles.layer

  return createPortal(
    <div
      className={layerClassName}
      style={{ ...brandStyle, ...pinStyle }}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose()
        }
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="landing-share-sheet-title"
      >
        <div className={styles.handle} aria-hidden />
        <div className={styles.head}>
          <button
            type="button"
            className={styles.close}
            aria-label="Fechar"
            onClick={handleClose}
            disabled={overlayBusy}
          >
            ×
          </button>
          <h4 id="landing-share-sheet-title" className={styles.title}>
            Compartilhe e Ganhe
          </h4>
          <p className={styles.subtitle}>
            Escolha a melhor forma de divulgar a campanha.
          </p>
        </div>

        <div className={styles.balance} aria-label="Resumo da roleta">
          <div>
            <strong>{balance?.available_spins ?? '—'}</strong>
            <span>giros disponíveis</span>
          </div>
          <div>
            <strong>{balance?.paid_purchases ?? '—'}</strong>
            <span>compras pagas</span>
          </div>
          <div>
            <strong>{balance?.shares_available ?? '—'}</strong>
            <span>compartilhamentos com bônus</span>
          </div>
        </div>
        <p className={styles.rule}>
          {balance?.achievement_spins_earned &&
          balance.roulette_unlocked === false &&
          balance.shares_available === 0
            ? 'Você ganhou giros por uma conquista. Compartilhe para liberar sua roleta.'
            : balance?.spins_per_share
              ? `Cada compra paga permite 1 compartilhamento com bônus de ${balance.spins_per_share} ${balance.spins_per_share === 1 ? 'giro' : 'giros'}. Você já usou ${balance.shares_used ?? 0}.`
              : 'Nesta campanha, compartilhar não libera giros extras.'}
        </p>

        {storyInput ? (
          <div className={styles.storyAction}>
            <span className={styles.storyKicker}>UM CONVITE COM A SUA CARA</span>
            <ParticipantShareButton
              {...storyInput}
              officialShareUrl={payload.shareUrl}
              label="Compartilhar meu card da jornada"
              description="Imagem para Stories com seu avatar, conquistas e link da campanha."
              onShareAction={() => {
                setActionStarted(true)
                setActionHint(
                  'Publique ou envie seu card e volte para confirmar o compartilhamento.'
                )
              }}
            />
          </div>
        ) : null}

        <div className={styles.socialGrid}>
          {SOCIAL_CHANNELS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.socialBtn}
              disabled={overlayBusy}
              onClick={() => void handleChannel(item.id)}
            >
              <span className={styles.socialIcon}>
                <Icon icon={item.icon} aria-hidden />
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.actionList}>
          <button
            type="button"
            className={`${styles.actionItem}${copyHighlight ? ` ${styles.actionCopied}` : ''}`}
            disabled={overlayBusy}
            onClick={() => void handleChannel('copy')}
          >
            <span className={styles.actionIcon} aria-hidden>
              {copyHighlight ? '✓' : '⧉'}
            </span>
            <span className={styles.actionBody}>
              <span className={styles.actionTitle}>
                {copyHighlight ? 'Copiado!' : 'Copiar link'}
              </span>
              <span className={styles.actionDesc}>
                Copie o endereço público da campanha.
              </span>
            </span>
          </button>

          <button
            type="button"
            className={styles.actionItem}
            disabled={overlayBusy}
            onClick={() => void handleChannel('copy_message')}
          >
            <span className={styles.actionIcon} aria-hidden>
              <Icon icon="mdi:content-copy" />
            </span>
            <span className={styles.actionBody}>
              <span className={styles.actionTitle}>Copiar convite completo</span>
              <span className={styles.actionDesc}>
                Texto da campanha e link para enviar.
              </span>
            </span>
          </button>

          <button
            type="button"
            className={styles.actionItem}
            disabled={overlayBusy}
            onClick={() => void handleChannel('more')}
          >
            <span className={styles.actionIcon} aria-hidden>
              ⋮
            </span>
            <span className={styles.actionBody}>
              <span className={styles.actionTitle}>Mais opções</span>
              <span className={styles.actionDesc}>
                Use o compartilhamento nativo do seu dispositivo quando estiver
                disponível.
              </span>
            </span>
          </button>
        </div>

        {actionHint ? (
          <p className={styles.actionHint} role="status">
            {actionHint}
          </p>
        ) : null}

        {canUnlock ? (
          <button
            type="button"
            className={styles.confirm}
            disabled={!actionStarted || overlayBusy}
            onClick={() => void confirm()}
          >
            {confirmBusy
              ? 'Registrando…'
              : balance?.spins_per_share && balance.shares_available > 0
                ? `Já compartilhei · liberar ${balance.spins_per_share} ${balance.spins_per_share === 1 ? 'giro' : 'giros'}`
                : 'Já compartilhei · liberar roleta'}
          </button>
        ) : (
          <p className={styles.noBonus}>
            {balance?.spins_per_share === 0
              ? 'Você pode divulgar a campanha, mas esta roleta não concede giros por compartilhamento.'
              : 'Compartilhar continua disponível. Para ganhar novos giros por compartilhamento, aguarde uma nova compra paga.'}
          </p>
        )}
        {confirmBusy ? (
          <div className={styles.feedbackLoading}>
            <span className={styles.loader} aria-hidden />
            <span>{busyLabel}</span>
          </div>
        ) : null}

        {sheetError ? <div className={styles.feedbackError}>{sheetError}</div> : null}
      </div>
    </div>,
    document.body
  )
}
